import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Lock, KeyRound, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authApi, readError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Three-step flow: request code → enter code + new password → auto sign in.
const ForgotPassword = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [step, setStep] = useState("request"); // "request" | "reset"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  const requestCode = async (e) => {
    e?.preventDefault?.();
    if (!email) return toast.error("Please enter your registered email.");
    setBusy(true);
    try {
      await authApi.forgotPassword(email);
      toast.success("If that email is registered, a 6-digit reset code was sent.");
      setStep("reset");
    } catch (err) {
      toast.error(readError(err) || "Could not send reset code.");
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) return toast.error("The reset code is 6 digits.");
    if (newPassword.length < 8)
      return toast.error("Please choose a password of at least 8 characters.");
    setBusy(true);
    try {
      const user = await authApi.resetPassword(email, code, newPassword);
      setUser?.(user);
      toast.success("Password reset. You are now signed in.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(readError(err) || "Could not reset password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-6 py-12" data-testid="page-forgot-password">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="rounded-3xl border border-gray-100 bg-white p-8 shadow-sm"
      >
        <Link
          to="/login"
          className="mb-4 inline-flex items-center gap-1 text-xs font-medium text-apicon-navy/70 hover:text-apicon-navy"
          data-testid="forgot-back-to-login"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
        </Link>
        <h1 className="font-display text-2xl font-bold text-apicon-navy">Reset your password</h1>
        <p className="mt-1 text-sm text-gray-500">
          {step === "request"
            ? "Enter your registered email — we'll send a 6-digit code."
            : `Enter the 6-digit code we sent to ${email}.`}
        </p>

        {step === "request" ? (
          <form onSubmit={requestCode} className="mt-6 space-y-4">
            <div>
              <Label className="text-sm text-apicon-navy" htmlFor="forgot-email">Registered email</Label>
              <div className="relative mt-1">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 rounded-xl border-gray-200 pl-10 text-sm focus-visible:ring-apicon-navy"
                  data-testid="forgot-input-email"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="h-12 w-full rounded-full bg-apicon-navy text-base font-semibold text-white hover:bg-apicon-navy/90"
              data-testid="forgot-send-code"
            >
              {busy ? "Sending…" : "Send reset code"}
            </Button>
          </form>
        ) : (
          <form onSubmit={submitReset} className="mt-6 space-y-4">
            <div>
              <Label className="text-sm text-apicon-navy" htmlFor="forgot-code">6-digit code</Label>
              <div className="relative mt-1">
                <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  id="forgot-code"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="h-12 rounded-xl border-gray-200 pl-10 text-center text-lg font-mono tracking-[0.4em] focus-visible:ring-apicon-navy"
                  data-testid="forgot-input-code"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm text-apicon-navy" htmlFor="forgot-new-password">New password</Label>
              <div className="relative mt-1">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  id="forgot-new-password"
                  type={showPwd ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="h-12 rounded-xl border-gray-200 pl-10 pr-10 text-sm focus-visible:ring-apicon-navy"
                  data-testid="forgot-input-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2 text-gray-400 hover:text-apicon-navy"
                  aria-label={showPwd ? "Hide password" : "Show password"}
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="h-12 w-full rounded-full bg-apicon-navy text-base font-semibold text-white hover:bg-apicon-navy/90"
              data-testid="forgot-submit-reset"
            >
              {busy ? "Resetting…" : "Reset password & sign in"}
            </Button>
            <button
              type="button"
              onClick={() => setStep("request")}
              className="w-full text-center text-xs text-apicon-navy/70 hover:text-apicon-navy"
              data-testid="forgot-resend-code"
            >
              Didn't receive it? Send a new code
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
